<template>
  <div class="block sm:flex justify-between items-center bg-gray-900 bg-opacity-80 px-4 py-2 text-white">
    <div class="flex justify-between space-x-1 text-xs sm:text-sm">
      <div class="text-sm sm:text-nase font-semibold text-indigo-200 flex items-center gap-2">
      </div>
      <div class="flex items-center space-x-1">
        <select
          v-model="sortPreference"
          @change="saveSortPreference"
          class="bg-gray-700 text-indigo-200 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 border border-gray-600"
        >
          <option value="type">เรียงตามประเภท</option>
          <option value="inventory">เรียงตามลำดับในคลัง</option>
        </select>
        <button
          @click="toggleTransferFilter"
          class="py-1 px-2 bg-gray-700 hover:bg-gray-600 border border-gray-600 rounded-lg transition-all duration-200 flex items-center"
          :class="{ 'bg-indigo-600 hover:bg-indigo-500 border-indigo-700': showTransferOnly }"
          title="แสดงเฉพาะไอเทมที่โอนได้"
        >
          <i class="fa-solid fa-right-left text-sm" :class="{ 'text-green-500': showTransferOnly, 'text-gray-500': !showTransferOnly }"></i>
        </button>
        <button
          @click="openSortModal('storage')"
          class="py-1 px-2 bg-indigo-600 hover:bg-indigo-500 border border-indigo-700 rounded-lg transition-all duration-200 flex items-center gap-2"
          :class="{ 'bg-gray-600 opacity-0.6 cursor-not-allowed': isSorting || loading }"
          :disabled="isSorting || loading"
        >
          <span v-if="!isSorting"><i class="fas fa-sort text-sm"></i></span>
          <i v-else class="fas fa-spinner animate-spin text-indigo-200 text-sm"></i>
          <div class="hidden sm:block">จัดเรียง</div>
        </button>
        <button
          @click="showStorageUpgradeModal = true"
          class="flex items-center space-x-1 py-1 px-2 bg-indigo-600 hover:bg-indigo-500 border border-indigo-700 rounded-lg transition-all duration-200 gap-2"
          :class="{ 'bg-gray-600 opacity-0.6 cursor-not-allowed': loading }"
          :disabled="loading"
        >
          <i class="fas fa-plus text-sm font-bold"></i>
          <div class="hidden sm:block">อัปเกรด</div>
        </button>
        <button
          @click="showDiamondModal = true"
          class="flex items-center space-x-1 py-1 px-2 bg-indigo-600 hover:bg-indigo-500 border border-indigo-700 rounded-lg transition-all duration-200 gap-2"
          :class="{ 'bg-gray-600 opacity-0.6 cursor-not-allowed': loading }"
          :disabled="loading"
        >
          <i class="fas fa-gem text-sm font-bold"></i>
          <div class="hidden sm:block">จัดการเพชร</div>
        </button>
        <button
          v-if="storageItems.length > 0"
          @click="showMoveToBagModal = true"
          class="bg-indigo-600 hover:bg-indigo-700 text-sm text-white py-1 px-3 rounded-md transition-colors duration-200 flex items-center gap-2"
          :disabled="loading || storageItems.length === 0"
        >
          <i class="fas fa-suitcase text-xs"></i> <span class="hidden sm:block">ย้ายไปกระเป๋าทั้งหมด</span>
        </button>
      </div>
    </div>
  </div>
  <div class="bg-gray-900 px-4 py-2">
    <p class="text-sm text-gray-400">น้ำหนัก: {{ storageWeight }} / {{ storageMaxLoad }}</p>

    <!-- Filter และ Search -->
    <div class="flex gap-4 text-sm">
      <select
        v-model="selectedType"
        class="bg-gray-700 text-white rounded-lg p-2 w-1/3 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all duration-200"
      >
        <option value="">ทุกประเภท</option>
        <option v-for="type in typeOptions" :key="type" :value="type">{{ type }}</option>
      </select>
      <input
        v-model="searchQuery"
        type="text"
        placeholder="ค้นหาไอเทม..."
        class="bg-gray-700 text-white rounded-lg p-2 w-2/3 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all duration-200"
      />
    </div>
  </div>
  <div class="p-3 pb-16 bg mb-auto h-full overflow-auto bg-gradient-to-b from-gray-800 text-white" id="style-1">
    <!-- ส่วนคลัง -->
    <div>
      <div v-if="loading" class="flex items-center justify-center py-6">
        <i class="fas fa-spinner animate-spin text-indigo-200 text-4xl"></i>
      </div>
      <div v-else-if="error" class="text-center text-red-300 py-6 text-sm">
        {{ error }}
      </div>
      <div v-else-if="filteredStorageItems.length > 0" class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
        <div
          v-for="item in filteredStorageItems"
          :key="item.instanceId"
          @click="selectStorageItem(item)"
          class="bg-gray-900 bg-opacity-80 border border-gray-800 rounded-lg p-3 cursor-pointer hover:bg-gray-800 hover:shadow-lg transition-all duration-200"
          :class="{ 'border-2 border-indigo-500': selectedStorageItem && selectedStorageItem.instanceId === item.instanceId }"
        >
          <img
            :src="item.img || 'default-item.png'"
            alt="Item"
            class="w-16 h-16 mx-auto rounded-md object-cover border-2 mb-2"
            :class="item.rarityColors?.borderColor || 'border-indigo-500'"
            @error="handleImageError"
          />
          <div class="flex items-center justify-center gap-1">
            <p
              class="text-sm font-semibold text-center"
              :class="item.rarityColors?.textColor || 'text-white'"
            >
              {{ item.type === 'หนังสือ' ? `${bookTypeOrder[item.name] || ''}. ` : '' }}{{ item.name }}<span v-if="item.level">+{{ item.level }}</span>
              <span v-if="item.quantity > 1">x{{ item.quantity }}</span>
            </p>
            <i
              v-if="item.transfer"
              class="fa-solid fa-arrow-right-arrow-left text-green-300 text-xs"
            ></i>
          </div>
          <p class="text-sm text-gray-400 text-center">น้ำหนัก: {{ (item.weight || 0) * (item.quantity || 1) }}</p>
          <div v-if="selectedStorageItem && selectedStorageItem.instanceId === item.instanceId" class="flex flex-col gap-2 mt-2">
            <button
              @click.stop="moveToBag"
              class="text-xs sm:text-sm w-full bg-green-600 hover:bg-green-500 border border-green-700 text-white p-1.5 rounded-lg transition-all duration-200 flex items-center justify-center gap-2"
              :class="{ 'bg-gray-600 opacity-0.6 cursor-not-allowed': isMoving }"
              :disabled="isMoving"
            >
              <span v-if="!isMoving"><i class="fas fa-arrow-down "></i> นำออก</span>
              <i v-else class="fas fa-spinner animate-spin text-indigo-200 text-sm"></i>
            </button>
          </div>
        </div>
      </div>
      <p v-else class="text-gray-400 text-center py-6 text-sm">ไม่มีไอเทมในคลังที่ตรงเงื่อนไข</p>
    </div>
  </div>
 <!-- Storage Upgrade Modal -->
    <transition name="fade">
      <div
        v-if="showStorageUpgradeModal"
        class="fixed inset-0 bg-gray-900 bg-opacity-80 flex items-center justify-center z-50"
      >
        <div class="bg-gray-800 p-6 rounded-lg border border-gray-700 shadow-xl w-full max-w-md">
          <div class="flex items-center gap-2 mb-4">
            <i class="fas fa-warehouse text-white text-lg"></i>
            <h3 class="text-lg font-semibold text-white">อัปเกรดคลังเก็บของ</h3>
          </div>
          <p class="text-sm text-gray-400 mb-4">เพิ่มขนาดคลังเพื่อเก็บของได้มากขึ้น</p>
          <div class="bg-gray-800 border border-gray-700 p-3 rounded-lg mb-4">
            <p class="text-sm text-gray-400 flex items-center gap-2">
              <i class="fas fa-weight-hanging text-white text-sm"></i> ขนาดปัจจุบัน: {{ storageMaxLoad }} / {{ selectedUpgradeType === 'cash' ? 700 : 50 }}
            </p>
          </div>
          <div class="mb-4">
            <p class="text-sm text-gray-400 mb-2">เลือกวิธีการอัปเกรด:</p>
            <div class="flex gap-4">
              <label class="flex items-center gap-2">
                <input
                  type="radio"
                  v-model="selectedUpgradeType"
                  value="cash"
                  class="text-indigo-500 focus:ring-indigo-500"
                />
                <span class="text-sm text-white">ใช้ Cash (ถึง 700)</span>
              </label>
              <label class="flex items-center gap-2">
                <input
                  type="radio"
                  v-model="selectedUpgradeType"
                  value="resources"
                  class="text-indigo-500 focus:ring-indigo-500"
                />
                <span class="text-sm text-white">ใช้ทรัพยากร (ถึง 50)</span>
              </label>
            </div>
          </div>
          <div class="bg-gray-800 border border-gray-700 p-3 rounded-lg mb-4">
            <p v-if="selectedUpgradeType === 'cash'" class="text-sm text-gray-400">
              <span class="flex items-center gap-2">
                <i class="fas fa-money-bill text-green-300 text-sm"></i> ค่าใช้จ่าย: {{ nextCashCost }} Cash
              </span>
              <span class="text-sm text-gray-400">ผลลัพธ์: +10 หน่วยให้ขนาดคลัง</span>
            </p>
            <p v-else-if="nextResourceUpgrade" class="text-sm text-gray-400">
              ค่าใช้จ่าย:
              <span class="flex items-center gap-2">
                <i class="fas fa-coins text-gray-300 text-sm"></i> Silver: {{ nextResourceUpgrade.silver }}
              </span>
              <span class="flex items-center gap-2" v-if="nextResourceUpgrade.gold > 0">
                <i class="fas fa-gem text-yellow-300 text-sm"></i> Gold: {{ nextResourceUpgrade.gold }}
              </span>
              <span class="flex items-center gap-2" v-if="nextResourceUpgrade.diamond > 0">
                <i class="fas fa-gem text-blue-300 text-sm"></i> Diamond: {{ nextResourceUpgrade.diamond }}
              </span>
              <span class="text-sm text-gray-400">ผลลัพธ์: +10 หน่วยให้ขนาดคลัง</span>
            </p>
            <p v-else class="text-sm text-gray-400">ไม่มีการอัปเกรดเพิ่มเติม</p>
          </div>
          <div
            v-if="selectedUpgradeType === 'cash' && storageMaxLoad >= 700"
            class="text-yellow-300 mb-4 flex items-center gap-2 text-sm"
          >
            <i class="fas fa-exclamation-triangle text-yellow-300 text-sm"></i> ถึงขีดจำกัดสูงสุดแล้ว!
          </div>
          <div
            v-else-if="selectedUpgradeType === 'resources' && storageMaxLoad >= 50"
            class="text-yellow-300 mb-4 flex items-center gap-2 text-sm"
          >
            <i class="fas fa-exclamation-triangle text-yellow-300 text-sm"></i> ถึงขีดจำกัดสูงสุดสำหรับการอัปเกรดด้วยทรัพยากรแล้ว!
          </div>
          <div
            v-else-if="selectedUpgradeType === 'cash' && cash < nextCashCost"
            class="text-red-300 mb-4 flex items-center gap-2 text-sm"
          >
            <i class="fas fa-exclamation-circle text-red-300 text-sm"></i> เงินไม่เพียงพอ!
          </div>
          <div
            v-else-if="selectedUpgradeType === 'resources' && nextResourceUpgrade && (silver < nextResourceUpgrade.silver || gold < nextResourceUpgrade.gold || diamond < nextResourceUpgrade.diamond)"
            class="text-red-300 mb-4 flex items-center gap-2 text-sm"
          >
            <i class="fas fa-exclamation-circle text-red-300 text-sm"></i> ทรัพยากรไม่เพียงพอ!
          </div>
          <div v-if="error" class="text-red-300 mb-4 text-sm">{{ error }}</div>
          <div class="flex justify-end space-x-2">
            <button
              @click="upgradeStorage"
              class="py-2 px-4 bg-indigo-600 hover:bg-indigo-500 border border-indigo-700 text-white rounded-lg transition-all duration-200 flex items-center gap-2"
              :class="{ 'bg-gray-600 opacity-0.6 cursor-not-allowed': isUpgrading || !canUpgrade }"
              :disabled="isUpgrading || !canUpgrade"
            >
              <span v-if="!isUpgrading"><i class="fas fa-arrow-up text-sm"></i> อัปเกรด</span>
              <i v-else class="fas fa-spinner animate-spin text-indigo-200 text-sm"></i>
            </button>
            <button
              @click="showStorageUpgradeModal = false"
              class="py-2 px-4 bg-gray-600 hover:bg-gray-500 border border-gray-600 text-white rounded-lg transition-all duration-200 flex items-center gap-2"
            >
              <i class="fas fa-times text-sm"></i> ยกเลิก
            </button>
          </div>
        </div>
      </div>
    </transition>

    <diamond-management-modal
      :show="showDiamondModal"
      :diamond="diamond"
      :diamond-stored="diamondStored"
      :weekly-deposited-diamond="weeklyDepositedDiamond"
      :error="error"
      @update:show="showDiamondModal = $event"
      @update:error="error = $event"
    />

    <!-- Sort Order Modal -->
    <transition name="fade">
      <div
        v-if="showSortModal"
        class="fixed inset-0 bg-gray-900 bg-opacity-80 flex items-center justify-center z-50"
      >
        <div class="bg-gray-800 p-6 rounded-lg border border-gray-700 shadow-xl w-full max-w-md">
          <div class="flex items-center gap-2 mb-4">
            <i class="fas fa-sort text-white text-lg"></i>
            <h3 class="text-lg font-semibold text-white">กำหนดลำดับการจัดเรียง</h3>
          </div>
          <p class="text-sm text-gray-400 mb-4">ใช้ปุ่มเพื่อจัดลำดับประเภทไอเทม (เรียงตามประเภทและความหายากตามตัวอักษร)</p>
          <div class="max-h-64 overflow-y-auto mb-4">
            <transition-group tag="ul" name="sort-transition" class="space-y-2">
              <li
                v-for="(type, index) in sortOrder"
                :key="type"
                class="flex items-center justify-between bg-gray-700 p-2 rounded-lg"
              >
                <button
                  @click="moveUp(index)"
                  class="p-1 text-gray-300 hover:text-white w-10"
                  :disabled="index === 0"
                >
                  <i class="fas fa-arrow-up"></i>
                </button>
                <span class="text-sm text-white flex-1 text-center">{{ type }}</span>
                <button
                  @click="moveDown(index)"
                  class="p-1 text-gray-300 hover:text-white w-10"
                  :disabled="index === sortOrder.length - 1"
                >
                  <i class="fas fa-arrow-down"></i>
                </button>
              </li>
            </transition-group>
          </div>
          <div v-if="error" class="text-red-300 mb-4 text-sm">{{ error }}</div>
          <div class="flex justify-end space-x-2">
            <button
              @click="resetSortOrder"
              class="py-2 px-4 bg-yellow-600 hover:bg-yellow-500 border border-yellow-700 text-white rounded-lg transition-all duration-200 flex items-center gap-2"
            >
              <i class="fas fa-undo text-sm"></i> คืนค่าเริ่มต้น
            </button>
            <button
              @click="saveSortOrder"
              class="py-2 px-4 bg-indigo-600 hover:bg-indigo-500 border border-indigo-700 text-white rounded-lg transition-all duration-200 flex items-center gap-2"
              :class="{ 'bg-gray-600 opacity-0.6 cursor-not-allowed': isSorting }"
              :disabled="isSorting"
            >
              <span v-if="!isSorting"><i class="fas fa-save text-sm"></i> บันทึกและจัดเรียง</span>
              <i v-else class="fas fa-spinner animate-spin text-indigo-200 text-sm"></i>
            </button>
            <button
              @click="showSortModal = false"
              class="py-2 px-4 bg-gray-600 hover:bg-gray-500 border border-gray-600 text-white rounded-lg transition-all duration-200 flex items-center gap-2"
            >
              <i class="fas fa-times text-sm"></i> ยกเลิก
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- Detail Item Modal -->
    <detail-item-modal
      v-if="showDetailModal"
      :itemId="selectedStorageItem.id"
      :item="selectedStorageItem"
      @close="showDetailModal = false"
    />

    <!-- Move To Bag Modal -->
    <move-to-bag-modal
      v-if="showMoveToBagModal"
      :items="storageItems"
      @close="showMoveToBagModal = false"
      @items-moved="handleItemsMoved"
    />
</template>

<script>
import { ref, onMounted, onUnmounted, computed, watch } from 'vue';
import { projectFirestore, projectFunctions } from '@/firebase/config';
import getUser from '@/composables/getUser';
import { usePlayerStore } from '@/composables/usePlayerStore';
import { useInventoryStore } from '@/composables/useInventoryStore';
import { isOldSchema, triggerReMigration } from '@/composables/useMigrationGuard';
import DiamondManagementModal from '@/components/DiamondManagementModal.vue';
import DetailItemModal from '@/components/DetailItemModal.vue';
import MoveToBagModal from '@/components/character/MoveToBagModal.vue'; // Assume this is the path for the new modal
import { books } from '@/constants/books';
import gameItemsData from '@/data/gameItems.json';

export default {
  components: {
    DiamondManagementModal,
    DetailItemModal,
    MoveToBagModal
  },
  setup() {
    const { user } = getUser();
    const storageItems = ref([]);
    const storageWeight = ref(0);
    const storageMaxLoad = ref(20);
    const cash = ref(0);
    const silver = ref(0);
    const gold = ref(0);
    const diamond = ref(0);
    const diamondStored = ref(0);
    const weeklyDepositedDiamond = ref(0);
    const loading = ref(true);
    const error = ref(null);
    const selectedStorageItem = ref(null);
    const isMoving = ref(false);
    const isSorting = ref(false);
    const isUpgrading = ref(false);
    const selectedType = ref('');
    const searchQuery = ref('');
    const showTransferOnly = ref(false);
    const showStorageUpgradeModal = ref(false);
    const showSortModal = ref(false);
    const showDiamondModal = ref(false);
    const showDetailModal = ref(false);
    const showMoveToBagModal = ref(false);
    const sortPreference = ref('type');
    const selectedUpgradeType = ref('resources');
    const playerStore = usePlayerStore();
    const inventoryStore = useInventoryStore();

    const resourceUpgradeCosts = [
      { maxLoad: 30, silver: 1000, gold: 0, diamond: 0 },
      { maxLoad: 40, silver: 3000, gold: 0, diamond: 0 },
      { maxLoad: 50, silver: 5000, gold: 0, diamond: 0 },
    ];

    const nextCashCost = computed(() => {
      if (storageMaxLoad.value >= 600) return 50;
      if (storageMaxLoad.value >= 500) return 40;
      if (storageMaxLoad.value >= 300) return 30;
      return 20;
    });

    const nextResourceUpgrade = computed(() => {
      return resourceUpgradeCosts.find((cost) => cost.maxLoad === storageMaxLoad.value + 10) || null;
    });

    const canUpgrade = computed(() => {
      if (selectedUpgradeType.value === 'cash') {
        return storageMaxLoad.value < 700 && cash.value >= nextCashCost.value;
      } else {
        return (
          storageMaxLoad.value < 50 &&
          nextResourceUpgrade.value &&
          silver.value >= nextResourceUpgrade.value.silver &&
          gold.value >= nextResourceUpgrade.value.gold &&
          diamond.value >= nextResourceUpgrade.value.diamond
        );
      }
    });

     const defaultSortOrder = [
      'พาหนะ', 'รักษา', 'กัปดัก', 'อาหาร', 'แผนที่','หุ่นยนต์สังหาร', 'ปลดกัปดัก','ระเบิด','ปล้น', 'การ์ด', 'หนังสือ', 'อวัยวะจักรกล', 'วิญญาณ' , 'หมวก', 'แว่นตา', 'เสื้อ', 'เสื้อคลุม', 'เกราะ', 'ปลอกแขน', 'ถุงมือ', 'กางเกง',
      'สนับแข้ง', 'รองเท้า', 'เข็มขัด', 'สร้อยคอ', 'แหวน', 'อาวุธ', 'อาวุธสำรอง',
      'ตะกรุด', 'ผ้ายันต์', 'สายสิญจน์', 'วัสดุวิญญาณ','วัสดุ','กล่อง','ทั่วไป' 
    ];
    const sortOrder = ref([...defaultSortOrder]);
    const typeOptions = [...defaultSortOrder];

    const bookTypeOrder = {};
    const typeCounts = {};
    books.forEach(book => {
      if (!typeCounts[book.type]) typeCounts[book.type] = 0;
      typeCounts[book.type]++;
      bookTypeOrder[book.name] = typeCounts[book.type];
    });

    const loadSortOrder = () => {
      try {
        const savedOrder = localStorage.getItem('sortOrder');
        if (savedOrder) {
          const parsedOrder = JSON.parse(savedOrder);
          if (parsedOrder.length === defaultSortOrder.length &&
              defaultSortOrder.every(type => parsedOrder.includes(type))) {
            sortOrder.value = parsedOrder;
          }
        }
      } catch (err) {
        console.error('Error loading sort order from localStorage:', err);
        error.value = 'ไม่สามารถโหลดลำดับการจัดเรียงจากเครื่อง: ' + err.message;
      }
    };

    const saveSortOrderToLocal = () => {
      try {
        localStorage.setItem('sortOrder', JSON.stringify(sortOrder.value));
      } catch (err) {
        console.error('Error saving sort order to localStorage:', err);
        error.value = 'ไม่สามารถบันทึกลำดับการจัดเรียงในเครื่อง: ' + err.message;
      }
    };

    const loadSortPreference = () => {
      try {
        const savedPreference = localStorage.getItem('storageSortPreference');
        if (savedPreference && ['type', 'inventory'].includes(savedPreference)) {
          sortPreference.value = savedPreference;
        }
      } catch (err) {
        console.error('Error loading sort preference from localStorage:', err);
        error.value = 'ไม่สามารถโหลดการตั้งค่าการเรียงลำดับจากเครื่อง: ' + err.message;
      }
    };

    const saveSortPreference = () => {
      try {
        localStorage.setItem('storageSortPreference', sortPreference.value);
      } catch (err) {
        console.error('Error saving sort preference to localStorage:', err);
        error.value = 'ไม่สามารถบันทึกการตั้งค่าการเรียงลำดับลงในเครื่อง: ' + err.message;
      }
    };

    const resetSortOrder = () => {
      sortOrder.value = [...defaultSortOrder];
      saveSortOrderToLocal();
    };

    watch(() => inventoryStore.storageData, async (storageData) => {
      if (!storageData) {
        storageItems.value = [];
        storageWeight.value = 0;
        storageMaxLoad.value = 20;
        diamondStored.value = 0;
        weeklyDepositedDiamond.value = 0;
        loading.value = false;
        return;
      }

      if (isOldSchema(storageData)) {
        await triggerReMigration();
        return;
      }

      storageWeight.value = storageData.currentWeight || 0;
      storageMaxLoad.value = storageData.maxLoad || 20;
      diamondStored.value = storageData.diamond || 0;
      weeklyDepositedDiamond.value = storageData.weeklyDepositedDiamond || 0;
      const itemsObj = storageData.items || {};
      let hasInvalid = false;
      const lockedItemIds = [];
      const mappedItems = Object.entries(itemsObj).map(([instanceId, itemData]) => {
        if (!itemData || !itemData.templateId) {
          hasInvalid = true;
          return null;
        }
        const template = gameItemsData[itemData.templateId] || {};
        const states = itemData.states || {};
        if (states.locked === true) {
          lockedItemIds.push(instanceId);
        }
        return {
          id: instanceId,
          instanceId,
          templateId: itemData.templateId,
          states,
          ...template,
          ...states,
          name: template.name || itemData.templateId,
          level: states.level,
          rarity: states.rarity ?? template.rarity ?? 1,
          rarityColors: states.rarityColors ?? template.rarityColors,
          transfer: states.transfer ?? template.transfer ?? false,
          decomposable: states.decomposable ?? template.decomposable ?? false,
        };
      }).filter(item => item !== null);
      storageItems.value = mappedItems;

      // Auto-unlock items in storage (locked items should not exist in storage)
      if (lockedItemIds.length > 0) {
        try {
          const gameActions = projectFunctions.httpsCallable('gameActions');
          await gameActions({
            action: 'toggleItemsLock',
            instanceIds: lockedItemIds,
            locked: false,
            source: 'storage',
          });
          mappedItems.forEach(item => {
            if (lockedItemIds.includes(item.instanceId)) {
              item.states.locked = false;
              item.locked = false;
            }
          });
        } catch (unlockErr) {
          console.error('Auto-unlock failed:', unlockErr);
        }
      }

      const calculatedWeight = mappedItems.reduce((acc, item) => acc + ((item.weight || 0) * (item.quantity || 1)), 0);
      if (calculatedWeight !== storageWeight.value || hasInvalid) {
        const gameActions = projectFunctions.httpsCallable('gameActions');
        await gameActions({ action: 'recalculateWeight', target: 'storage' });
      }

      loading.value = false;
    }, { immediate: true });

    watch(() => playerStore.userData, (data) => {
      cash.value = data?.cash || 0;
    }, { immediate: true });

    watch(() => playerStore.characterData, (data) => {
      if (data) {
        silver.value = data.silver || 0;
        gold.value = data.gold || 0;
        diamond.value = data.diamond || 0;
      } else {
        silver.value = 0;
        gold.value = 0;
        diamond.value = 0;
      }
    }, { immediate: true });

    const toggleTransferFilter = () => {
      showTransferOnly.value = !showTransferOnly.value;
    };

    const filteredStorageItems = computed(() => {
      let items = [...storageItems.value];
      if (sortPreference.value === 'type') {
        const typeOrder = sortOrder.value.reduce((acc, type, index) => {
          acc[type] = index + 1;
          return acc;
        }, {});
        items.sort((a, b) => {
          const orderA = typeOrder[a.type] || 999;
          const orderB = typeOrder[b.type] || 999;
          if (orderA !== orderB) {
            return orderA - orderB;
          }
          const rarityA = a.rarity ? String(a.rarity) : '';
          const rarityB = b.rarity ? String(b.rarity) : '';
          if (rarityA !== rarityB) {
            return rarityA.localeCompare(rarityB, 'th');
          }
          return a.name.localeCompare(b.name, 'th');
        });
      } else {
        // Sort by inventory order
        return items; // Simplified: Handle async sorting if needed
      }
      return items.filter((item) => {
        const matchesType = selectedType.value ? item.type === selectedType.value : true;
        const matchesSearch = searchQuery.value
          ? item.name.toLowerCase().includes(searchQuery.value.toLowerCase())
          : true;
        const matchesTransfer = showTransferOnly.value ? item.transfer === true : true;
        return matchesType && matchesSearch && matchesTransfer;
      });
    });

    const selectStorageItem = (item) => {
      if (isMoving.value) return; // ป้องกันการคลิกไอเทมขณะกำลังนำออก (moveToBag) เพื่อหลีกเลี่ยงบัค

      if (selectedStorageItem.value && selectedStorageItem.value.instanceId === item.instanceId) {
        openDetailModal();
      } else {
        selectedStorageItem.value = item;
        error.value = '';
      }
    };

    const moveToBag = async () => {
      if (!user.value || !selectedStorageItem.value) return;

      isMoving.value = true;
      try {
        const gameActions = projectFunctions.httpsCallable('gameActions');
        await gameActions({ action: 'moveItemToBag', instanceId: selectedStorageItem.value.instanceId });
        // Local update for continuity
        storageItems.value = storageItems.value.filter(item => item.instanceId !== selectedStorageItem.value.instanceId);
        storageWeight.value -= (selectedStorageItem.value.weight || 0) * (selectedStorageItem.value.quantity || 1);
        selectedStorageItem.value = null;
      } catch (err) {
        error.value = err.message || 'เกิดข้อผิดพลาดในการย้ายไอเทมไปกระเป๋า';
      } finally {
        isMoving.value = false;
      }
    };

    const handleItemsMoved = (movedInstanceIds) => {
      let removedWeight = 0;
      storageItems.value = storageItems.value.filter(item => {
        if (movedInstanceIds.includes(item.instanceId)) {
          removedWeight += (item.weight || 0) * (item.quantity || 1);
          return false;
        }
        return true;
      });
      storageWeight.value -= removedWeight;
      selectedStorageItem.value = null;
      showMoveToBagModal.value = false;
    };

    const openSortModal = () => {
      showSortModal.value = true;
      error.value = '';
    };

    const moveUp = (index) => {
      if (index === 0) return;
      const newOrder = [...sortOrder.value];
      [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];
      sortOrder.value = newOrder;
    };

    const moveDown = (index) => {
      if (index === sortOrder.value.length - 1) return;
      const newOrder = [...sortOrder.value];
      [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
      sortOrder.value = newOrder;
    };

    const saveSortOrder = async () => {
      if (!user.value) return;

      isSorting.value = true;
      try {
        saveSortOrderToLocal();
        showSortModal.value = false;
      } catch (err) {
        error.value = err.message || 'เกิดข้อผิดพลาดในการบันทึกการจัดเรียง';
      } finally {
        isSorting.value = false;
      }
    };

    const upgradeStorage = async () => {
      if (!user.value || !canUpgrade.value) return;

      isUpgrading.value = true;
      try {
        const gameActions = projectFunctions.httpsCallable('gameActions');
        await gameActions({ action: 'upgradeStorage', upgradeType: selectedUpgradeType.value });
        showStorageUpgradeModal.value = false;
      } catch (err) {
        error.value = err.message || 'เกิดข้อผิดพลาดในการอัปเกรดคลัง';
      } finally {
        isUpgrading.value = false;
      }
    };

    const openDetailModal = () => {
      if (selectedStorageItem.value) {
        showDetailModal.value = true;
      }
    };

    const handleImageError = (event) => {
      event.target.src = 'default-item.png';
    };

    onMounted(() => {
      loadSortOrder();
      loadSortPreference();
    });

    return {
      storageItems,
      storageWeight,
      storageMaxLoad,
      cash,
      silver,
      gold,
      diamond,
      diamondStored,
      weeklyDepositedDiamond,
      loading,
      error,
      selectedStorageItem,
      isMoving,
      isSorting,
      isUpgrading,
      selectedType,
      searchQuery,
      showTransferOnly,
      showStorageUpgradeModal,
      showSortModal,
      showDiamondModal,
      showDetailModal,
      showMoveToBagModal,
      sortOrder,
      sortPreference,
      selectedUpgradeType,
      nextResourceUpgrade,
      nextCashCost,
      canUpgrade,
      bookTypeOrder,
      selectStorageItem,
      moveToBag,
      openSortModal,
      moveUp,
      moveDown,
      saveSortOrder,
      resetSortOrder,
      upgradeStorage,
      saveSortPreference,
      handleImageError,
      toggleTransferFilter,
      openDetailModal,
      handleItemsMoved,
      typeOptions,
      filteredStorageItems
    };
  },
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
#style-1::-webkit-scrollbar,
#storage-scroll::-webkit-scrollbar {
  width: 5px;
}
#style-1::-webkit-scrollbar-track,
#storage-scroll::-webkit-scrollbar-track {
  background: #374151;
  border-radius: 10px;
}
#style-1::-webkit-scrollbar-thumb,
#storage-scroll::-webkit-scrollbar-thumb {
  background: #4b5563;
  border-radius: 10px;
}
#style-1::-webkit-scrollbar-thumb:hover,
#storage-scroll::-webkit-scrollbar-thumb:hover {
  background: #6b7280;
}
#style-1,
#storage-scroll {
  scrollbar-width: thin;
  scrollbar-color: #4b5563 #374151;
}
.bg {
  background: url('https://firebasestorage.googleapis.com/v0/b/zombiekub.firebasestorage.app/o/bg%2Fstorage.png?alt=media&token=daf4cd00-d1ae-4333-a2f8-2ebd46a43eac') no-repeat center center fixed,
              linear-gradient(to bottom, rgba(31, 41, 55, 0.8), rgba(0, 0, 0, 0.9));
}
.sort-transition-move {
  transition: transform 0.3s ease;
}
.sort-transition-enter-active,
.sort-transition-leave-active {
  transition: all 0.3s ease;
}
.sort-transition-enter-from,
.sort-transition-leave-to {
  opacity: 0;
  transform: translateY(20px);
}
</style>
